// Contrato de prestação de serviços aceito eletronicamente na matrícula e na compra de pacotes.
// O texto final (já com nome, CPF e objeto preenchidos) é gravado junto do registro, então o
// contrato que o aluno leu nunca muda sozinho, mesmo se o modelo for editado depois.
//
// O modelo é editável no painel (config/contrato), mas só nos trechos listados em ContratoTextos e
// nos dados da contratada. Título, numeração e títulos das cláusulas, o quadro das partes e a cláusula
// de aceite eletrônico são fixos para o texto não perder a formatação nem o valor legal.

export interface ContratoTextos {
    objetoCurso: string;
    objetoPacote: string;
    pagamentoCurso: string;
    pagamentoPacote: string;
    obrigacoesContratada: string;
    obrigacoesContratante: string;
    propriedade: string;
    protecao: string;
}

export interface ContratoContratada {
    nome: string;
    cpf: string;
    endereco: string;
}

export interface ContratoConfig {
    // Versão do texto publicado. Cada publicação soma 1.
    versao: number;
    // Versão mínima que o aluno precisa ter aceitado. Se for maior que a do aceite dele, o modal aparece.
    versaoExigida: number;
    contratada: ContratoContratada;
    foro: string;
    textos: ContratoTextos;
    atualizadoEm?: number;
    atualizadoPor?: string;
}

// Marcador substituído pelo nome do curso ou do pacote nas cláusulas de objeto.
export const MARCADOR_OBJETO = '{{objeto}}';
// Marcadores opcionais da cláusula de pagamento do curso: valor da matrícula e máximo de parcelas do curso.
export const MARCADOR_MATRICULA = '{{valorMatricula}}';
export const MARCADOR_PARCELAS = '{{maxParcelas}}';
export const MARCADOR_VALOR_CURSO = '{{valorCurso}}';
// Máximo de parcelas assumido quando o curso não tem o campo preenchido.
export const MAX_PARCELAS_PADRAO = 10;

export const CONTRATO_PADRAO: ContratoConfig = {
    versao: 1,
    versaoExigida: 1,
    contratada: {
        nome: 'Prof. Elias Ancelmo',
        cpf: '080.881.904-60',
        endereco: 'Rua Projetada 01 (Lot. Recanto do Lago), nº 86, Bairro Juá, Limoeiro/PE, CEP 55706-387',
    },
    foro: 'Limoeiro/PE',
    textos: {
        objetoCurso: `Este contrato tem por objeto a prestação de serviços educacionais de redação pela CONTRATADA, por meio do curso "${MARCADOR_OBJETO}", incluindo as aulas, os materiais de apoio e as correções de redação previstas na turma contratada, na modalidade (presencial ou online) escolhida na matrícula.`,
        objetoPacote: `Este contrato tem por objeto a aquisição do pacote de correções de redação "${MARCADOR_OBJETO}", que dá direito ao envio de redações para correção pela equipe do Núcleo de Redação, na quantidade e nas condições descritas na página do pacote no momento da compra.`,
        pagamentoCurso: `O valor do curso é composto por (i) uma taxa de matrícula, cujo valor é ${MARCADOR_MATRICULA}, paga no ato da matrícula, e (ii) o valor do curso, no total de ${MARCADOR_VALOR_CURSO}, que pode ser parcelado em até ${MARCADOR_PARCELAS} parcelas mensais, conforme o plano escolhido pelo CONTRATANTE na matrícula, no qual constam o número de parcelas, o valor de cada uma e as datas de vencimento. O pagamento pode ser feito por PIX ou pelos demais meios disponibilizados pela CONTRATADA. A matrícula só é efetivada após a confirmação do pagamento da taxa de matrícula. O atraso no pagamento das parcelas pode resultar no bloqueio do acesso à plataforma até a regularização. O CONTRATANTE pode solicitar o cancelamento a qualquer momento, diretamente à CONTRATADA, sem multa, com efeito a partir do mês seguinte ao já pago.`,
        pagamentoPacote: 'O valor do pacote é pago uma única vez, por PIX, conforme as instruções exibidas na compra. A liberação do pacote ocorre após a confirmação do pagamento pela CONTRATADA. As redações do pacote devem ser utilizadas dentro do ano letivo vigente, salvo acordo diferente entre as partes.',
        obrigacoesContratada: 'A CONTRATADA se compromete a prestar o serviço contratado com zelo e qualidade, a disponibilizar acesso à plataforma Núcleo de Redação e a corrigir as redações enviadas dentro do prazo e dos critérios informados ao CONTRATANTE.',
        obrigacoesContratante: 'O CONTRATANTE se compromete a fornecer dados verdadeiros, a manter o sigilo do seu login e senha e a utilizar a plataforma apenas para fins pessoais. O acesso é pessoal e intransferível.',
        propriedade: 'Todo o conteúdo disponibilizado (aulas, materiais, propostas e correções) é de uso pessoal do CONTRATANTE, sendo vedados o compartilhamento de login e a reprodução, distribuição ou revenda do material, sob pena de suspensão do acesso.',
        protecao: 'Os dados pessoais fornecidos pelo CONTRATANTE serão tratados pela CONTRATADA exclusivamente para fins de cadastro, cobrança e prestação do serviço contratado, em conformidade com a Lei nº 13.709/2018 (LGPD) e com a Política de Privacidade da plataforma.',
    },
};

export const versaoTexto = (config: Pick<ContratoConfig, 'versao'>) => `v${config.versao}`;

// "v2" e "v1-2026-10" (formato antigo) viram 2 e 1. Sem versão gravada, 0.
export const versaoNumero = (versao?: string) => {
    const m = /^v(\d+)/.exec(versao ?? '');
    return m ? parseInt(m[1], 10) : 0;
};

// O aluno precisa aceitar (ou aceitar de novo) quando não tem contrato ou a versão dele é mais antiga que a exigida.
export const precisaAceitarContrato = (
    aluno: { contractText?: string; contractVersion?: string },
    config: Pick<ContratoConfig, 'versaoExigida'>
) => !aluno.contractText || versaoNumero(aluno.contractVersion) < config.versaoExigida;

const umaLinha = (s: string) => s.replace(/\s*\n\s*/g, ' ').trim();

export const formatCpfContrato = (cpf: string) => {
    const digitos = cpf.replace(/\D/g, '');
    return digitos.length === 11 ? digitos.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : cpf.trim();
};

export interface ContratoParams {
    tipo: 'matricula' | 'pacote';
    nome: string;
    cpf: string;
    // Matrícula: nome do curso; pacote: nome do pacote.
    objeto: string;
    // Matrícula: valor da matrícula do curso (marcador {{valorMatricula}}).
    valor?: string;
    // Matrícula: máximo de parcelas do curso (marcador {{maxParcelas}}). Sem valor, usa MAX_PARCELAS_PADRAO.
    maxParcelas?: number;
    // Matrícula: valor total do curso (marcador {{valorCurso}}). Sem valor, a frase sai sem ele.
    valorCurso?: string;
    config?: ContratoConfig;
}

export function buildContractText({ tipo, nome, cpf, objeto, valor, maxParcelas, valorCurso, config = CONTRATO_PADRAO }: ContratoParams): string {
    const { contratada, foro, textos } = config;
    const contratante = nome.trim() || '[nome do contratante]';
    const doc = formatCpfContrato(cpf) || '[CPF do contratante]';
    const servico = valor ? `${objeto} - ${valor}` : objeto;
    const ehPacote = tipo === 'pacote';
    const t = (s: string) => umaLinha(s);

    const objetoClausula = t(ehPacote ? textos.objetoPacote : textos.objetoCurso).split(MARCADOR_OBJETO).join(objeto);
    const parcelas = maxParcelas && maxParcelas > 0 ? maxParcelas : MAX_PARCELAS_PADRAO;
    const semValorCurso = valorCurso?.trim()
        ? t(ehPacote ? textos.pagamentoPacote : textos.pagamentoCurso).split(MARCADOR_VALOR_CURSO).join(valorCurso.trim())
        // Sem o valor total no curso, tira "no total de {{valorCurso}}" para a frase não ficar com um buraco.
        : t(ehPacote ? textos.pagamentoPacote : textos.pagamentoCurso).replace(/,?\s*no total de \{\{valorCurso\}\}/g, '').split(MARCADOR_VALOR_CURSO).join('o informado na matrícula');
    const pagamentoClausula = semValorCurso
        .split(MARCADOR_MATRICULA).join(valor?.trim() || 'o informado na matrícula')
        .split(MARCADOR_PARCELAS).join(String(parcelas));

    return `CONTRATO DE PRESTAÇÃO DE SERVIÇOS EDUCACIONAIS

CONTRATADA: ${umaLinha(contratada.nome)}, CPF ${umaLinha(contratada.cpf)}, com endereço na ${umaLinha(contratada.endereco)}.
CONTRATANTE: ${contratante}, CPF ${doc}.
SERVIÇO CONTRATADO: ${servico}.

1. OBJETO
${objetoClausula}

2. PAGAMENTO E VIGÊNCIA
${pagamentoClausula}

3. OBRIGAÇÕES DA CONTRATADA
${t(textos.obrigacoesContratada)}

4. OBRIGAÇÕES DO CONTRATANTE
${t(textos.obrigacoesContratante)}

5. PROPRIEDADE INTELECTUAL E USO DO CONTEÚDO
${t(textos.propriedade)}

6. PROTEÇÃO DE DADOS PESSOAIS
${t(textos.protecao)}

7. FORO
Fica eleito o foro da comarca de ${umaLinha(foro)} para dirimir quaisquer controvérsias decorrentes deste contrato.

8. ACEITE ELETRÔNICO
Ao marcar a caixa de aceite e ${ehPacote ? 'concluir a compra' : 'finalizar a matrícula'}, o CONTRATANTE declara ter lido e concordado integralmente com os termos acima, o que equivale à assinatura eletrônica deste contrato, nos termos do art. 10, § 2º, da Medida Provisória nº 2.200-2/2001.`;
}

// Valida o que a equipe digitou antes de publicar. Devolve a mensagem do primeiro problema ou null.
export function validarContratoConfig(c: Pick<ContratoConfig, 'contratada' | 'foro' | 'textos'>): string | null {
    if (!c.contratada.nome.trim()) return 'Informe o nome da contratada.';
    if (!c.contratada.cpf.trim()) return 'Informe o CPF da contratada.';
    if (!c.contratada.endereco.trim()) return 'Informe o endereço da contratada.';
    if (!c.foro.trim()) return 'Informe o foro (cidade/UF).';
    const rotulos: Record<keyof ContratoTextos, string> = {
        objetoCurso: '1. Objeto (curso)',
        objetoPacote: '1. Objeto (pacote)',
        pagamentoCurso: '2. Pagamento (curso)',
        pagamentoPacote: '2. Pagamento (pacote)',
        obrigacoesContratada: '3. Obrigações da contratada',
        obrigacoesContratante: '4. Obrigações do contratante',
        propriedade: '5. Propriedade intelectual',
        protecao: '6. Proteção de dados',
    };
    for (const chave of Object.keys(rotulos) as (keyof ContratoTextos)[]) {
        const texto = c.textos[chave];
        if (!texto.trim()) return `O texto da cláusula "${rotulos[chave]}" não pode ficar vazio.`;
        if (texto.length > 3000) return `O texto da cláusula "${rotulos[chave]}" está muito longo (máximo 3000 caracteres).`;
        // Uma linha começando com "N. Algo" seria lida como uma cláusula nova e quebraria o contrato.
        if (texto.split('\n').some((l) => /^\s*\d+\.\s+\S/.test(l))) return `A cláusula "${rotulos[chave]}" tem uma linha começando com número e ponto (ex: "1. "), o que quebra a formatação. Reescreva sem numerar.`;
    }
    if ((c.textos.objetoCurso.match(/\{\{objeto\}\}/g) ?? []).length !== 1) return `A cláusula "${rotulos.objetoCurso}" precisa conter exatamente uma vez o marcador ${MARCADOR_OBJETO} (é onde entra o nome do curso).`;
    if ((c.textos.objetoPacote.match(/\{\{objeto\}\}/g) ?? []).length !== 1) return `A cláusula "${rotulos.objetoPacote}" precisa conter exatamente uma vez o marcador ${MARCADOR_OBJETO} (é onde entra o nome do pacote).`;
    return null;
}

// Separa o texto salvo em: partes (cabeçalho) e cláusulas numeradas "N. TÍTULO".
export function parseContract(text: string) {
    const parties: string[] = [];
    const sections: { title: string; body: string }[] = [];
    let current: { title: string; body: string } | null = null;
    for (const line of text.split('\n').slice(1)) {
        if (/^\d+\. \S/.test(line)) {
            current = { title: line, body: '' };
            sections.push(current);
        } else if (current) {
            current.body += (current.body ? ' ' : '') + line.trim();
        } else if (line.trim()) {
            parties.push(line.trim());
        }
    }
    return { parties, sections: sections.map((s) => ({ ...s, body: s.body.trim() })) };
}

// Nome e CPF da contratada como estavam no contrato assinado (o PDF usa isto, não o modelo atual).
export function contratadaDoTexto(text: string): { nome: string; cpf: string } {
    const linha = parseContract(text).parties.find((p) => p.startsWith('CONTRATADA:')) ?? '';
    const m = /^CONTRATADA:\s*(.*?),\s*CPF\s+([^,]+),/.exec(linha);
    return m ? { nome: m[1].trim(), cpf: m[2].trim() } : { nome: CONTRATO_PADRAO.contratada.nome, cpf: CONTRATO_PADRAO.contratada.cpf };
}

export function formatDateTime(iso?: string) {
    if (!iso) return '-';
    const d = new Date(iso);
    return `${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}
